/* =====================================================================
 * mockData.js — «Доходы и расходы» (ДАШБОРД, тёмная тема).
 *
 * ВАЖНО про архитектуру моков.
 * По постановке фронт НЕ считает статусы/проценты/доли/итоги/баланс —
 * всё приходит готовым из GET /api/budget/dashboard. Поэтому мок играет
 * роль бэкенда: он отдаёт ответ в форме контракта (summary, balance,
 * income_categories, expense_groups, financing, tree, dynamics, integrity).
 * Компоненты только рисуют. Когда поднимут реальный эндпоинт — меняем
 * тело функций в api.js на fetch, СОХРАНЯЯ форму ответа; компоненты не трогаем.
 *
 * Этот файл — Layer 1 (каркас). Здесь заполнены meta и «оболочка» dashboard
 * (пустые массивы секций + признак периода), чтобы каркас страницы отрисовался
 * со скелетонами/пустыми состояниями. Реальные значения секций добавляются
 * послойно в следующих итерациях.
 * ===================================================================== */

/* Допуск балансовых тождеств — единый для дашборда, АРМ и обоих макетов.
   0,001 тыс. ₸ = 1 тенге. НЕ хардкодить в компонентах — брать из ответа API. */
export const TOLERANCE = 0.001;

/* Пороги статусов. В легенде и на карточках берутся ТОЛЬКО отсюда (meta.thresholds),
   в компонентах не дублировать. */
export const THRESHOLDS = {
  inc: { good: 100, warn: 95 }, // доходы: ≥100 good, 95–100 warn, <95 bad
  exp: { good: 95, warn: 85 },  // расходы: ≥95 good, 85–95 warn, <85 bad
};

/* Список бюджетов (состав по таблице 62 СТПО, элемент 1).
   Первый пункт (consolidated) и b8 дают один ответ — см. §5.2 постановки. */
export const BUDGETS = [
  { k: 'consolidated', labelKey: 'bCons' },
  { k: '8', labelKey: 'b8' },
  { k: '3', labelKey: 'b3' },
  { k: '7', labelKey: 'b7' },
];

/* Отчётные периоды (годовые есть, квартальных пока нет).
   is_partial: 2026 — неполный период (данные на 31.05.2026). */
const YEAR_PERIODS = [
  { key: '2023', year: 2023, is_partial: false },
  { key: '2024', year: 2024, is_partial: false },
  { key: '2025', year: 2025, is_partial: false },
  { key: '2026', year: 2026, is_partial: true },
];

/* Квартальных выгрузок нет — список пуст, причина в empty_reason.
   Компонент по этому признаку (а не по хардкоду) рисует #quarterWarn. */
const QUARTER_PERIODS = [];

/* Дата среза для неполного периода (для подписи «на 31.05.2026»). */
export const REPORT_DATE = '31.05.2026';

/* --------------------------- META (мок ответа) --------------------------- */
export function metaResponse() {
  return {
    budgets: BUDGETS.map((b) => ({ k: b.k, label_key: b.labelKey })),
    periods: {
      year: YEAR_PERIODS,
      quarter: QUARTER_PERIODS,
    },
    // причина пустоты квартального списка (для плашки #quarterWarn)
    quarter_empty_reason: 'no_quarterly_uploads',
    thresholds: THRESHOLDS,
    tolerance: TOLERANCE,
    report_date: REPORT_DATE,
    // meta.region / grouping / unit нужны АРМ и выгрузке; на дашборд не выводятся,
    // но в ответе присутствуют.
    region: 'Жылыойский район',
    grouping: 'функциональная',
    unit: 'тыс. ₸',
  };
}

/* ------------------------ DASHBOARD (оболочка) --------------------------- */
/* Layer 1: секции пустые (заполним послойно). Каркас по ним отрисует
   скелетоны/пустые состояния и корректные подписи периода. */
export function dashboardResponse(params) {
  const periodType = params && params.periodType ? params.periodType : 'year';
  const report = params && params.report ? params.report : '2026';

  // Квартал выбран, а выгрузок нет — секций нет, только причина.
  if (periodType === 'quarter' && QUARTER_PERIODS.length === 0) {
    return {
      empty: true,
      empty_reason: 'no_quarterly_uploads',
      report: { key: report, is_partial: false, date: null },
      tolerance: TOLERANCE,
      thresholds: THRESHOLDS,
    };
  }

  const rp = YEAR_PERIODS.find((p) => p.key === report) || YEAR_PERIODS[YEAR_PERIODS.length - 1];

  return {
    empty: false,
    report: {
      key: rp.key,
      is_partial: rp.is_partial,
      date: rp.is_partial ? REPORT_DATE : null,
    },
    tolerance: TOLERANCE,
    thresholds: THRESHOLDS,

    // integrity: пока считаем период целостным (плашки не показываем)
    integrity: { ok: true, failed: [], missing_sections: [] },

    // ↓↓↓ ТЕЛА СЕКЦИЙ — заполняются в следующих слоях ↓↓↓
    summary: null,           // §5.5 — 4 карточки
    balance: null,           // §5.6 — resources[]/uses[]/balanced/residual
    income_categories: [],   // §5.7
    expense_groups: [],      // §5.8
    expense_total: null,     // §5.8 — строка «II. ЗАТРАТЫ» из отчёта
    financing: [],           // §5.9
    financing_checks: [],    // §5.9 — три строки контроля #finNote
    tree: [],                // §5.10 — по текущему tree_section
    dynamics: null,          // §5.11 — {chart, rows[]}
  };
}
